import React, { useMemo, useState } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Autocomplete,
  TextField,
} from '@mui/material';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { useAuth } from '../../hooks/useAuth';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

/** Static mock users for the admin "filter by user" dropdown until the real users API is wired up */
const STATIC_USERS = [
  { id: 1, name: 'John Carter' },
  { id: 2, name: 'Maria Lopez' },
  { id: 3, name: 'Sam Patel' },
  { id: 4, name: 'Aisha Khan' },
];

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'];

/** Static mock series, deterministic per-user-id so switching the filter shows visibly different bars */
function buildMockSeries(seed = 0) {
  return MONTH_LABELS.map((_, i) => 10 + ((seed + i * 7) % 25));
}

const chartOptions = {
  responsive: true,
  plugins: { legend: { display: false } },
  scales: { y: { beginAtZero: true } },
};

const PerformanceChartCard = ({ title, datasetLabel, color, userId }) => (
  <Grid item xs={12} md={6}>
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
          {title}
        </Typography>
        <Bar
          options={chartOptions}
          data={{
            labels: MONTH_LABELS,
            datasets: [
              {
                label: datasetLabel,
                data: buildMockSeries(userId || title.length),
                backgroundColor: color,
              },
            ],
          }}
        />
      </CardContent>
    </Card>
  </Grid>
);

const DashboardPerformanceCharts = () => {
  const { user } = useAuth();
  const [selectedUser, setSelectedUser] = useState(null);

  const activeUserId = useMemo(
    () => selectedUser?.id || user?.id,
    [selectedUser, user]
  );

  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="subtitle1" fontWeight={600} color="text.secondary">
          Performance
        </Typography>

        <Autocomplete
          size="small"
          options={STATIC_USERS}
          getOptionLabel={(option) => option.name}
          value={selectedUser}
          onChange={(_, value) => setSelectedUser(value)}
          sx={{ width: 240 }}
          renderInput={(params) => (
            <TextField {...params} label="Filter by user" placeholder="All users" />
          )}
        />
      </Box>

      <Grid container spacing={2}>
        <PerformanceChartCard
          title="Leads Performance"
          datasetLabel="Leads"
          color="#42a5f5"
          userId={activeUserId}
        />
        <PerformanceChartCard
          title="Closer Performance"
          datasetLabel="Closed Deals"
          color="#66bb6a"
          userId={activeUserId}
        />
        <PerformanceChartCard
          title="Lead Gen Performance"
          datasetLabel="Leads Generated"
          color="#ffa726"
          userId={activeUserId}
        />
        <PerformanceChartCard
          title="Role Performance"
          datasetLabel="Role Output"
          color="#ab47bc"
          userId={activeUserId}
        />
        <PerformanceChartCard
          title="My Performance"
          datasetLabel="My Output"
          color="#26a69a"
          userId={user?.id}
        />
      </Grid>
    </Box>
  );
};

export default DashboardPerformanceCharts;
